<template>
    <C1FormGroup class="af-attributes-typeahead">
        <div class="afu-flex afu-items-center afu-justify-between">
            <C1Label
                v-if="!hideLabel"
                :label-for="id"
            >
                {{ label || localLabel }}
            </C1Label>

            <C1WhatIsThis
                v-if="whatIsThis.primary_text"
                :primary-text="whatIsThis.primary_text"
                :secondary-text="whatIsThis.secondary_text"
                class="afu-mb-2"
            />
        </div>

        <C1Typeahead
            :id="id"
            :options="attributeOptionsSearched"
            :placeholder="localInputPlaceholder"
            label-key="value"
            :allow-new="allowNew"
            :disabled="disabled"
            clean-after-select
            @search="searchAttributes"
            @hit="addAttributeOption"
        />

        <C1Spinner v-if="spinner.get_attributes" />

        <div
            v-if="attributeOptionsSelected.length"
            class="afu-mt-5"
        >
            <C1Tag
                v-for="attribute in attributeOptionsSelected"
                :key="`${attribute.value}_${attribute.id}`"
                :removable="!disabled"
                @remove="detachAttributeOption(attribute)"
            >
                {{ attribute.value }}
            </C1Tag>
        </div>

        <div
            v-if="!hideNoResultMessage && !attributeOptionsSelected.length && !spinner.get_attributes"
            class="af-no-result-found afu-text-lg afu-font-semibold afu-mt-5 afu-text-neutral-dark"
        >
            <slot name="noResultText">
                {{ noResultText }}
            </slot>
        </div>
    </C1FormGroup>
</template>

<script>
    import { mapActions } from 'vuex';
    import C1WhatIsThis from '@/components/C1WhatIsThis';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import C1Spinner from '@/components/C1Components/C1Spinner';
    import C1Tag from '@/components/C1Components/C1Tag';
    import C1Label from '@/components/C1Components/C1Label';
    import C1Typeahead from '@/components/C1Components/C1Typeahead';

    export default {
        name: 'SeekerAttributesTypeahead',

        components: {
            C1Typeahead,
            C1Label,
            C1Spinner,
            C1FormGroup,
            C1WhatIsThis,
            C1Tag,
        },

        props: {
            attributeCode: {
                type: String,
                required: true,
            },

            attributeId: {
                type: Number,
                required: true,
            },

            inputPlaceholder: {
                type: String,
                default: '',
            },

            label: {
                type: String,
                default: '',
            },

            hideLabel: {
                type: Boolean,
                default: false,
            },

            allowNew: {
                type: Boolean,
                default: false,
            },

            hideNoResultMessage: {
                type: Boolean,
                default: false,
            },

            disabled: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                id: `attribute_typeahead_${this.attributeId}_${this._uid}`,
                localLabel: '',
                localInputPlaceholder: '',
                whatIsThis: {
                    primary_text: '',
                    secondary_text: '',
                },
                attributeOptionsSelected: [],
                attributeOptionsSearched: [],
                spinner: {
                    get_attributes: false,
                },
            };
        },

        computed: {
            attributeSorted() {
                const attr = JSON.parse(
                    JSON.stringify(this.attributeOptionsSelected),
                );
                return attr.sort((a, b) => {
                    if (a.slug < b.slug) {
                        return -1;
                    }
                    if (a.slug > b.slug) {
                        return 1;
                    }
                    return 0;
                });
            },

            noResultText() {
                return `No ${this.localLabel.toLowerCase()} added.`;
            },
        },

        created() {
            this.getAttributes();
            this.setLabels();
        },

        methods: {
            ...mapActions('attribute', {
                $_searchAttributes: 'searchAttributes',
            }),

            ...mapActions('me', {
                $_getAttributes: 'getAttributes',
                $_storeAttribute: 'storeAttribute',
                $_attachAttribute: 'attachAttribute',
                $_detachAttribute: 'detachAttribute',
            }),

            getAttributes() {
                this.spinner.get_attributes = true;
                this.$_getAttributes(this.attributeId)
                    .then(response => {
                        this.attributeOptionsSelected = response.data;
                    })
                    .finally(() => {
                        this.spinner.get_attributes = false;
                    });
            },

            setLabels() {
                const labels = {
                    9: {
                        label: `${this.$t('label.licenses_and_certifications')}`,
                        placeholder:
                            'Add licenses or certifications eg. forklift license, AWS Certified',
                        whatIsThis: {
                            primary_text:
                                'These are the professional or trade licenses or certifications awarded to a person verifying that they have the required skills or competencies for a job.',
                            secondary_text: '',
                        },
                    },
                    10: {
                        label: `${this.$t('label.skills')}`,
                        placeholder:
                            'Add skills eg. Time Management, Leadership, Microsoft Excel',
                        whatIsThis: {
                            primary_text: '',
                            secondary_text: '',
                        },
                    },
                    21: {
                        label: `${this.$t('label.perks_and_benefits')}`,
                        placeholder: `Add ${this.$t(
                            'label.perks_and_benefits',
                        )} eg. Flexible work hours`,
                        whatIsThis: {
                            primary_text: '',
                            secondary_text: '',
                        },
                    },
                    24: {
                        label: `${this.$t('label.languages')}`,
                        placeholder: `Add ${this.$t('label.languages')}`,
                        whatIsThis: {
                            primary_text: '',
                            secondary_text: '',
                        },
                    },
                };

                this.localLabel = labels[this.attributeId].label;
                this.localInputPlaceholder = labels[this.attributeId].placeholder;
                this.whatIsThis = labels[this.attributeId].whatIsThis;
            },

            searchAttributes(keyword) {
                this.$_searchAttributes({ code: this.attributeCode, keyword }).then(
                    response => {
                        this.attributeOptionsSearched = response.data;
                    },
                );
            },

            addAttributeOption(attributeOption) {
                if (!attributeOption) {
                    return;
                }

                if (!attributeOption?.id) {
                    const ifAttributeOptionExists = this.attributeOptionsSelected.filter(
                        o => o.value === attributeOption,
                    );
                    if (!ifAttributeOptionExists.length && this.allowNew) {
                        this.storeAttributeOption(attributeOption);
                    }
                } else {
                    const ifAttributeOptionExists = this.attributeOptionsSelected.filter(
                        o => o.id === attributeOption.id,
                    );
                    if (!ifAttributeOptionExists.length) {
                        this.attachAttributeOption(attributeOption);
                    }
                }
            },

            storeAttributeOption(attributeOption) {
                const data = {
                    attribute_option_value: attributeOption,
                };

                this.$_storeAttribute({ id: this.attributeId, data }).then(
                    response => {
                        this.attributeOptionsSelected.push(response.data);
                    },
                );
            },

            attachAttributeOption(attributeOption) {
                this.attributeOptionsSelected.push(attributeOption);

                const data = {
                    attribute_option_id: attributeOption.id,
                };

                this.$_attachAttribute({ id: this.attributeId, data }).catch(() => {
                    const loc = this.attributeOptionsSelected.findIndex(
                        o => o.id === attributeOption.id,
                    );
                    this.attributeOptionsSelected.splice(loc, 1);
                });
            },

            detachAttributeOption(attributeOption) {
                const attr = this.attributeOptionsSelected.findIndex(
                    o => o.id === attributeOption.id,
                );
                this.attributeOptionsSelected.splice(attr, 1);

                const data = {
                    attribute_option_ids: [attributeOption.id],
                };

                this.$_detachAttribute({ id: this.attributeId, data });
            },
        },
    };
</script>
